import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useMemo, useRef, useState } from 'react';
import { createContainPaintModifier, TColumn } from '@/utils/dnd-utils.ts';

import { cn } from '@/utils/utils.ts';
import { Badge } from '@/components/ui/badge.tsx';
import { ScrollArea } from '@/components/ui/scroll-area.tsx';
import { Skeleton } from '@/components/ui/skeleton.tsx';
import { getLabelPlantStatus } from '@/features/plants/schemas/plants.utils.ts';
import { TPlantWithAccessControl } from './user-plant-access-management.tsx';
import {
  UserPlantAccessDesktopToolbar,
  useUserPlantAccessDesktopFilters,
} from './user-plant-access-management-desktop-toolbar.tsx';

const defaultCols: TColumn[] = [
  {
    id: 'available-plants',
    title: 'Available plants',
  },
  {
    id: 'associated-plants',
    title: 'Associated plants',
  },
];

function PlantCard({
  plant,
  disabled,
}: {
  plant: TPlantWithAccessControl;
  disabled?: boolean;
}) {
  const { setNodeRef, attributes, listeners, isDragging } = useSortable({
    id: plant.plantId,
    data: {
      type: 'Plant',
      plant,
    },
    disabled,
  });

  return (
    <div
      title={isDragging ? undefined : plant.basicDetails.name}
      ref={setNodeRef}
      {...(isDragging || disabled ? {} : { ...attributes, ...listeners })}
      className={cn(
        'relative flex items-center justify-between gap-2 rounded-lg bg-slate-100 px-4 py-2',
        disabled ? 'cursor-default opacity-50' : 'cursor-grab',
        isDragging
          ? 'border-primary border-2 border-dashed opacity-40'
          : !disabled && 'hover:ring-primary hover:ring-2 hover:ring-inset'
      )}
    >
      <span className="my-auto truncate select-none">
        {plant.basicDetails.name}
        {plant.newlyAdded && (
          <span className="ml-1 inline-block size-2 rounded-full bg-blue-400"></span>
        )}
      </span>
      <div className="flex items-center gap-2">
        {plant.basicDetails.status !== 'Operational' && (
          <Badge variant={getLabelPlantStatus(plant.basicDetails.status)} className="shrink-0">
            {plant.basicDetails.status}
          </Badge>
        )}
        {!disabled && (
          <button>
            <svg viewBox="0 0 20 20" width="12">
              <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z"></path>
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

function ColumnContainer({
  column,
  plants,
  previewPlant,
  disableItems,
}: {
  column: TColumn;
  plants: TPlantWithAccessControl[];
  previewPlant?: TPlantWithAccessControl | null;
  disableItems?: boolean;
}) {
  const plantIds = useMemo(() => {
    return plants.map((plant) => plant.plantId);
  }, [plants]);

  const { setNodeRef } = useSortable({
    id: column.id,
    data: {
      type: 'Column',
      column,
    },
  });

  return (
    <div ref={setNodeRef} className="w-full rounded-lg bg-slate-50 p-2">
      {/* Column title */}
      <h3 className="rounded-sm bg-slate-100 p-2 select-none">
        <span className="rounded-md bg-slate-200 px-2 py-1">{plants?.length}</span>{' '}
        {column.title}
      </h3>

      {/* Column plant container */}
      <ScrollArea className="mt-2 flex grow flex-col gap-4">
        <SortableContext strategy={verticalListSortingStrategy} items={plantIds}>
          <div className="flex h-[400px] flex-col gap-2">
            {previewPlant && (
              <div className="border-primary flex items-center justify-between gap-2 rounded-lg border-2 border-dashed bg-slate-100 px-4 py-2 opacity-40">
                <span className="truncate select-none">{previewPlant.basicDetails.name}</span>
                {previewPlant.basicDetails.status !== 'Operational' && (
                  <Badge
                    variant={getLabelPlantStatus(previewPlant.basicDetails.status)}
                    className="shrink-0"
                  >
                    {previewPlant.basicDetails.status}
                  </Badge>
                )}
              </div>
            )}
            {plants.map((plant) => (
              <PlantCard key={plant.plantId} plant={plant} disabled={disableItems} />
            ))}
          </div>
        </SortableContext>
      </ScrollArea>
    </div>
  );
}

export function UserPlantsAccessManagementDesktop({
  plants,
  onMovePlant,
  isLoading,
  canAssign,
}: {
  plants: TPlantWithAccessControl[];
  onMovePlant: (plantId: string, targetColumnId: string) => void;
  isLoading: boolean;
  canAssign: boolean;
}) {
  const [columns, setColumns] = useState<TColumn[]>(defaultCols);
  const columnsId = useMemo(() => columns.map((col) => col.id), [columns]);

  const [activePlant, setActivePlant] = useState<TPlantWithAccessControl | null>(null);
  const [dragOverColumnId, setDragOverColumnId] = useState<string | null>(null);
  const plantFilters = useUserPlantAccessDesktopFilters(plants);
  const containerRef = useRef<HTMLDivElement>(null);
  const containPaintModifier = useMemo(() => createContainPaintModifier(containerRef), []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  if (isLoading)
    return (
      <div className="grid h-full max-h-[470px] w-full grid-cols-2 gap-4">
        <Skeleton />
        <Skeleton />
      </div>
    );

  return (
    <div className="flex h-full flex-col gap-2">
      <UserPlantAccessDesktopToolbar filters={plantFilters} />
      <div
        ref={containerRef}
        className="flex w-full overflow-hidden"
        style={{ contain: 'paint' }}
      >
        <DndContext
          sensors={sensors}
          onDragStart={onDragStart}
          onDragOver={onDragOver}
          onDragEnd={onDragEnd}
          onDragCancel={onDragCancel}
        >
          <SortableContext items={columnsId}>
            <div className="grid flex-1 grid-cols-2 gap-4">
              {columns.map((col) => (
                <ColumnContainer
                  key={col.id}
                  column={col}
                  plants={plantFilters.filteredPlants.filter(
                    (plant) => plant.columnId === col.id
                  )}
                  previewPlant={
                    activePlant &&
                    dragOverColumnId === col.id &&
                    activePlant.columnId !== col.id
                      ? activePlant
                      : null
                  }
                  disableItems={!canAssign && col.id === 'available-plants'}
                />
              ))}
            </div>
          </SortableContext>

          <DragOverlay modifiers={[containPaintModifier]}>
            {activePlant && <PlantCard plant={activePlant} />}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );

  function onDragStart(event: DragStartEvent) {
    if (event.active.data.current?.type === 'Plant') {
      setActivePlant(event.active.data.current.plant);
    }
  }

  function onDragOver(event: DragOverEvent) {
    const { active, over } = event;
    if (!over || active.data.current?.type !== 'Plant') return;

    let targetColumnId: string | undefined;

    if (over.data.current?.type === 'Column') {
      targetColumnId = over.id as string;
    } else if (over.data.current?.type === 'Plant') {
      targetColumnId = (over.data.current.plant as TPlantWithAccessControl).columnId;
    }

    if (targetColumnId) {
      setDragOverColumnId(targetColumnId);
    }
  }

  function onDragCancel() {
    setDragOverColumnId(null);
    setActivePlant(null);
  }

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    const targetColumn = dragOverColumnId;
    setDragOverColumnId(null);
    setActivePlant(null);

    if (!over) return;
    if (active.id === over.id) return;

    if (active.data.current?.type === 'Column') {
      setColumns((columns) => {
        const activeColumnIndex = columns.findIndex((col) => col.id === active.id);
        const overColumnIndex = columns.findIndex((col) => col.id === over.id);
        return arrayMove(columns, activeColumnIndex, overColumnIndex);
      });
      return;
    }

    if (active.data.current?.type === 'Plant' && targetColumn) {
      onMovePlant(active.id as string, targetColumn);
    }
  }
}
